/**
 * Phase 2 — Add Contacts side panel.
 *
 * Two paths:
 *   1. Manual — paste LinkedIn URLs / emails (one per line) or upload CSV
 *   2. AI batch — count + cost estimate; creates a prospect_batch row that
 *      falcon-people's BatchProspectorWorker picks up
 *
 * Either entry point can run with custom filters (mandatory when no campaign
 * is in scope, optional override otherwise).
 *
 * Per spec: this is a side panel, not a modal. Closes on backdrop click.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import type { PipelineFilters, CampaignIcp } from '@/lib/services/campaigns';
import { autoMapHeaders, parseCsv, parsePastedLines, type ContactField } from '@/lib/csv';

type ImportedRow = Partial<Record<ContactField, string>>;

/** Fields an import can fill, in the order they are offered for mapping. */
const IMPORT_FIELDS: { field: ContactField; label: string }[] = [
  { field: 'email', label: 'Email' },
  { field: 'firstName', label: 'First name' },
  { field: 'lastName', label: 'Last name' },
  { field: 'title', label: 'Job title' },
  { field: 'company', label: 'Company' },
  { field: 'companyWebsite', label: 'Company website' },
  { field: 'linkedinUrl', label: 'LinkedIn URL' },
  { field: 'location', label: 'Location' },
  { field: 'phone', label: 'Phone' },
];

export interface AddContactsPanelProps {
  open: boolean;
  onClose: () => void;
  /** Campaign-scoped variant. Either this or contactListId must be set. */
  campaignId?: string;
  /** Contact-list-scoped variant (no campaign). customFilters required. */
  contactListId?: number;
  /** Pre-fill from the campaign's resolved ICP filters (if any). */
  campaignIcp?: CampaignIcp | null;
  /** Called with the new batch's short_id once accepted. */
  onBatchCreated?: (shortId: string) => void;
  /** Called after contacts are imported, so the caller can refresh its table. */
  onImported?: () => void;
}

interface CostEstimate {
  minPence: number;
  maxPence: number;
  assumptions: { expectedProspects: number };
}

export function AddContactsPanel(props: AddContactsPanelProps) {
  const { open, onClose, campaignId, contactListId, campaignIcp, onBatchCreated, onImported } = props;
  const campaignFilters: PipelineFilters | null = campaignIcp?.resolved ?? null;
  const [tab, setTab] = useState<'manual' | 'ai'>('ai');
  const [count, setCount] = useState(50);
  const [overrideFilters, setOverrideFilters] = useState(false);
  const [customFiltersText, setCustomFiltersText] = useState('');
  const [estimate, setEstimate] = useState<CostEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [manualLines, setManualLines] = useState('');
  const [csv, setCsv] = useState<{ name: string; headers: string[]; rows: Record<string, string>[] } | null>(null);
  const [mapping, setMapping] = useState<ImportedRow>({});

  const filtersMandatory = !campaignId;
  const showFilterEditor = filtersMandatory || overrideFilters;

  useEffect(() => {
    if (!open) {
      setEstimate(null);
      setSubmitting(false);
      setEstimating(false);
    }
  }, [open]);

  const parseFilters = (): PipelineFilters | null => {
    if (!showFilterEditor) return null;
    if (!customFiltersText.trim()) return null;
    try {
      return JSON.parse(customFiltersText);
    } catch {
      return null;
    }
  };

  const runEstimate = async () => {
    const filters = parseFilters() ?? campaignFilters ?? null;
    if (!filters) {
      toast.error('Filters required to estimate. Add them or select a campaign with a resolved ICP.');
      return;
    }
    setEstimating(true);
    try {
      const res = await fetch('/api/prospect-batches/estimate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters, requestedCount: count }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = (await res.json()) as CostEstimate;
      setEstimate(data);
    } catch (err) {
      toast.error(`Estimate failed: ${(err as Error).message}`);
    } finally {
      setEstimating(false);
    }
  };

  const submitAiBatch = async () => {
    const customFilters = parseFilters();
    if (filtersMandatory && !customFilters) {
      toast.error('Custom filters are required when not running inside a campaign.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/prospect-batches', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: campaignId ?? null,
          contactListId: contactListId ?? null,
          requestedCount: count,
          customFilters: customFilters ?? undefined,
          estimatedCostPence: estimate?.maxPence,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      toast.success(`Batch ${data.shortId} queued.`);
      onBatchCreated?.(data.shortId);
      onClose();
    } catch (err) {
      toast.error(`Could not queue batch: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const readCsvFile = async (file: File) => {
    try {
      const { headers, rows } = parseCsv(await file.text());
      if (headers.length === 0 || rows.length === 0) {
        toast.error('That file has no rows.');
        return;
      }
      setCsv({ name: file.name, headers, rows });
      setMapping(autoMapHeaders(headers));
    } catch (err) {
      toast.error(`Could not read that file: ${(err as Error).message}`);
    }
  };

  /** CSV rows reduced to the mapped fields; pasted text parsed line by line. */
  const buildRows = (): ImportedRow[] => {
    if (!csv) return parsePastedLines(manualLines);
    return csv.rows.map((row) => {
      const out: ImportedRow = {};
      for (const [field, header] of Object.entries(mapping)) {
        if (header) out[field as ContactField] = row[header] ?? '';
      }
      return out;
    });
  };

  const submitManual = async () => {
    const rows = buildRows();
    if (rows.length === 0) {
      toast.error(csv ? 'No rows to import from that file.' : 'Paste at least one email or LinkedIn URL.');
      return;
    }
    if (csv && !mapping.email && !mapping.linkedinUrl) {
      toast.error('Pick which column holds the email address or LinkedIn URL.');
      return;
    }

    setSubmitting(true);
    try {
      const url = campaignId
        ? `/api/campaigns/${campaignId}/contacts/import`
        : `/api/contact-lists/${contactListId}/import`;
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacts: rows }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Import failed (${res.status})`);

      const skipped = (data.skippedInvalid ?? 0) + (data.skippedDuplicate ?? 0);
      const notes = [
        data.alreadyInList ? `${data.alreadyInList} already in the list` : null,
        data.skippedInvalid ? `${data.skippedInvalid} without an email or LinkedIn URL` : null,
        data.skippedDuplicate ? `${data.skippedDuplicate} repeated in the file` : null,
      ].filter(Boolean);

      if (data.added === 0 && skipped === rows.length) {
        toast.error('Nothing imported: no row had an email address or LinkedIn URL.');
        return;
      }
      toast.success(`Imported ${data.added} ${campaignId ? 'lead' : 'contact'}${data.added === 1 ? '' : 's'}.`, {
        description: notes.length ? notes.join(', ') : undefined,
      });
      onImported?.();
      onClose();
    } catch (err) {
      toast.error(`Import failed: ${(err as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-40 bg-scrim/30 flex justify-end"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[480px] max-w-full bg-card h-full overflow-y-auto p-6 shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold">{campaignId ? 'Add Leads' : 'Add Contacts'}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setTab('ai')}
            className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${
              tab === 'ai'
                ? 'bg-info-surface-strong text-info-foreground-strong'
                : 'bg-input-bg text-secondary-foreground'
            }`}
          >
            AI batch
          </button>
          <button
            onClick={() => setTab('manual')}
            className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${
              tab === 'manual'
                ? 'bg-info-surface-strong text-info-foreground-strong'
                : 'bg-input-bg text-secondary-foreground'
            }`}
          >
            Manual import
          </button>
        </div>

        {tab === 'ai' ? (
          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium">How many prospects?</label>
              <Input
                type="number"
                min={1}
                max={10_000}
                value={count}
                onChange={(e) => {
                  setCount(parseInt(e.target.value || '0', 10));
                  setEstimate(null);
                }}
                className="mt-1"
              />
            </div>

            {!filtersMandatory && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Override the campaign's ICP</p>
                  <p className="text-xs text-muted-foreground">
                    Use a different filter set just for this batch.
                  </p>
                </div>
                <Switch checked={overrideFilters} onCheckedChange={setOverrideFilters} />
              </div>
            )}

            {showFilterEditor && (
              <div>
                <label className="text-sm font-medium">
                  Custom filters {filtersMandatory && <span className="text-danger-text">*</span>}
                </label>
                <textarea
                  value={customFiltersText}
                  onChange={(e) => {
                    setCustomFiltersText(e.target.value);
                    setEstimate(null);
                  }}
                  rows={6}
                  placeholder='{ "sicCodes": ["62012"], "postTowns": ["LONDON"], "roleKeywords": ["CTO"] }'
                  className="mt-1 w-full font-mono text-xs p-3 rounded-lg border border-control-border bg-input-bg"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  PipelineFilters JSON. A friendlier editor lands with the Master Agent (Phase 4).
                </p>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <Button variant="secondary" onClick={runEstimate} disabled={estimating}>
                {estimating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Estimating</> : 'Estimate cost'}
              </Button>
              {estimate && (
                <p className="text-sm">
                  ~£{(estimate.minPence / 100).toFixed(2)}–£{(estimate.maxPence / 100).toFixed(2)}{' '}
                  <span className="text-xs text-muted-foreground">
                    ({estimate.assumptions.expectedProspects} prospects)
                  </span>
                </p>
              )}
            </div>

            <div className="pt-4 border-t border-border">
              <Button variant="primary" onClick={submitAiBatch} disabled={submitting} className="w-full">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Queueing</> : 'Queue batch'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {csv ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{csv.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {csv.rows.length} row{csv.rows.length === 1 ? '' : 's'}, {csv.headers.length} columns
                    </p>
                  </div>
                  <Button variant="secondary" size="xs" onClick={() => { setCsv(null); setMapping({}); }}>
                    Remove
                  </Button>
                </div>

                <div>
                  <p className="text-sm font-medium">Columns</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Matched from the headings. Change anything that looks wrong.
                  </p>
                  <div className="space-y-1.5">
                    {IMPORT_FIELDS.map(({ field, label }) => (
                      <div key={field} className="flex items-center gap-2">
                        <span className="text-sm w-36 shrink-0">{label}</span>
                        <select
                          value={mapping[field] ?? ''}
                          onChange={(e) =>
                            setMapping((prev) => ({ ...prev, [field]: e.target.value || undefined }))
                          }
                          className="flex-1 min-w-0 text-sm h-9 px-2 rounded-lg border border-control-border bg-input-bg"
                        >
                          <option value="">Not imported</option>
                          {csv.headers.map((header) => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium">Paste LinkedIn URLs or emails</label>
                  <textarea
                    value={manualLines}
                    onChange={(e) => setManualLines(e.target.value)}
                    rows={8}
                    placeholder="jane@example.com&#10;https://www.linkedin.com/in/john-doe"
                    className="mt-1 w-full text-sm p-3 rounded-lg border border-control-border bg-input-bg"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    One per line. Anything without an email or LinkedIn URL is skipped.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">or</span>
                  <span className="h-px flex-1 bg-border" />
                </div>

                <label className="block">
                  <span className="text-sm font-medium">Upload a CSV</span>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void readCsvFile(file);
                      e.target.value = '';
                    }}
                    className="mt-1 w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:text-sm file:bg-active file:text-primary hover:file:bg-accent cursor-pointer"
                  />
                </label>
              </>
            )}

            <Button variant="primary" onClick={submitManual} disabled={submitting} className="w-full">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Importing</> : 'Import'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
